import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    CalendarDaysIcon, 
    ChartBarIcon, 
    CreditCardIcon, 
    DocumentChartBarIcon,
    UserGroupIcon,
    ClockIcon,
    BellAlertIcon,
    CheckCircleIcon,
    ArrowRightIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';

function Marketing() {
    const navigate = useNavigate();

    const handleGetStartedClick = () => {
        navigate('/plans');
    };

    const handleViewPlansClick = () => {
        navigate('/plans');
    };

    const features = [
        {
            icon: CalendarDaysIcon,
            title: 'Sessions Management Made Simple',
            description: 'Stop juggling spreadsheets and text messages. Create sessions in seconds, manage capacity effortlessly, and let clients book themselves. Your training schedule stays organized while you focus on what you do best - training.',
            benefits: [
                'Quick session creation with flexible scheduling',
                'Automatic capacity management',
                'Client self-booking 24/7',
                'Recurring session templates'
            ]
        },
        {
            icon: DocumentChartBarIcon,
            title: 'Reports That Help You Stay Ahead',
            description: 'Know exactly who\'s coming to each session before you arrive. Track attendance patterns, identify trends, and plan your day with confidence. All the insights you need, none of the manual work.',
            benefits: [
                'Daily session attendance reports',
                'Client engagement tracking',
                'At-risk member identification',
                'Token usage analytics'
            ]
        },
        {
            icon: CreditCardIcon,
            title: 'Payment Integration That Just Works',
            description: 'Eliminate chasing payments and manual reconciliation. GoCardless integration handles subscriptions automatically, while you get real-time payment tracking and transparent transaction history.',
            benefits: [
                'Automated subscription billing',
                'Real-time payment tracking',
                'Transparent transaction history',
                'Reduced payment admin time'
            ]
        },
        {
            icon: UserGroupIcon,
            title: 'User Tracking & Management',
            description: 'Keep your finger on the pulse of your business. See which clients are thriving, who might need extra support, and how your membership is growing - all in one place.',
            benefits: [
                'Member engagement monitoring',
                'Attendance tracking per client',
                'Growth and retention metrics',
                'Automated member notifications'
            ]
        },
        {
            icon: ChartBarIcon,
            title: 'Business Reports & Insights',
            description: 'Make decisions based on data, not guesswork. Understand your busiest times, most popular sessions, and revenue trends. Everything you need to grow smarter, not just harder.',
            benefits: [
                'Venue attendance analytics',
                'Revenue tracking and forecasting',
                'Session popularity metrics',
                'Token distribution insights'
            ]
        },
        {
            icon: ClockIcon,
            title: 'Save Hours Every Week',
            description: 'What used to take hours now takes minutes. Automated bookings, instant notifications, and self-service client management mean you get your time back to do what matters.',
            benefits: [
                'Automated booking confirmations',
                'Self-service client portal',
                'Automatic cancellation handling',
                'Instant availability updates'
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-luxury-black">
            {/* Navigation */}
            <nav className="border-b border-luxury-graphite/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate('/')}>
                            <h1 className="text-xl sm:text-2xl font-display font-bold text-gradient-emerald">
                                WorldsHighStreet
                            </h1>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-4">
                            <button
                                onClick={() => navigate('/')}
                                className="text-emerald-300/80 hover:text-emerald-300 transition-colors text-sm sm:text-base font-medium"
                            >
                                Home
                            </button>
                            <button
                                onClick={handleViewPlansClick}
                                className="text-emerald-300/80 hover:text-emerald-300 transition-colors text-sm sm:text-base font-medium"
                            >
                                Plans
                            </button>
                            <a 
                                href="/login" 
                                className="text-emerald-300/80 hover:text-emerald-300 transition-colors text-sm sm:text-base font-medium"
                            >
                                Login
                            </a>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 via-transparent to-emerald-600/5"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 relative">
                    <div className="text-center max-w-4xl mx-auto">
                        <div className="inline-flex items-center gap-2 bg-emerald-600/10 border border-emerald-600/30 rounded-full px-4 py-2 mb-6">
                            <SparklesIcon className="h-5 w-5 text-emerald-300" />
                            <span className="text-emerald-300 text-sm font-medium">Built for Personal Trainers</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-luxury-pearl mb-6">
                            Make Your Personal Training{' '}
                            <span className="text-gradient-emerald">Business Easier</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-luxury-pearl/60 mb-8 max-w-3xl mx-auto">
                            Stop spending hours on admin. Focus on training. We handle the scheduling, payments, and reporting so you can grow your business without the headaches.
                        </p>
                        <button
                            onClick={handleGetStartedClick}
                            className="btn-primary text-base sm:text-lg px-8 py-3 inline-flex items-center gap-2 group"
                        >
                            Start Making Life Easier
                            <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="text-center mb-12 sm:mb-16">
                    <h2 className="text-3xl sm:text-4xl font-display font-bold text-gradient-emerald mb-4">
                        Everything You Need, Nothing You Don't
                    </h2>
                    <p className="text-lg text-luxury-pearl/60 max-w-3xl mx-auto">
                        We built this platform by listening to personal trainers. Every feature is designed to save you time and make your business run smoother.
                    </p>
                </div>

                <div className="space-y-16">
                    {features.map((feature, index) => {
                        const Icon = feature.icon;
                        const isEven = index % 2 === 0;
                        
                        return (
                            <div
                                key={index}
                                className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
                            >
                                <div className="flex-1">
                                    <div className="card-luxury p-6 sm:p-8 h-full">
                                        <div className="flex items-start gap-4 mb-4">
                                            <div className="flex-shrink-0">
                                                <div className="w-14 h-14 bg-emerald-600/10 rounded-xl flex items-center justify-center">
                                                    <Icon className="h-7 w-7 text-emerald-300" />
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-2xl sm:text-3xl font-display font-bold text-luxury-pearl mb-3">
                                                    {feature.title}
                                                </h3>
                                                <p className="text-luxury-pearl/70 text-base sm:text-lg leading-relaxed mb-6">
                                                    {feature.description}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-[72px]">
                                            {feature.benefits.map((benefit, idx) => (
                                                <div key={idx} className="flex items-start gap-2">
                                                    <CheckCircleIcon className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                                                    <span className="text-luxury-pearl/60 text-sm">{benefit}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* How It Works Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="card-luxury p-8 sm:p-12 bg-gradient-to-br from-emerald-600/10 to-transparent">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-display font-bold text-luxury-pearl mb-4">
                            How It Makes Your Life Easier
                        </h2>
                        <p className="text-lg text-luxury-pearl/60 max-w-2xl mx-auto">
                            Real examples of how WorldsHighStreet saves you time every single day
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-600/20 rounded-full mb-4">
                                <BellAlertIcon className="h-8 w-8 text-emerald-300" />
                            </div>
                            <h3 className="text-xl font-display font-bold text-luxury-pearl mb-2">
                                Morning Coffee
                            </h3>
                            <p className="text-luxury-pearl/60">
                                Check your phone. See exactly who's coming to your sessions today. No surprises, no last-minute calls.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-600/20 rounded-full mb-4">
                                <ClockIcon className="h-8 w-8 text-emerald-300" />
                            </div>
                            <h3 className="text-xl font-display font-bold text-luxury-pearl mb-2">
                                During Training
                            </h3>
                            <p className="text-luxury-pearl/60">
                                Client wants to book next week? They do it themselves while you're mid-burpee. No admin interruption.
                            </p>
                        </div>

                        <div className="text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-600/20 rounded-full mb-4">
                                <CreditCardIcon className="h-8 w-8 text-emerald-300" />
                            </div>
                            <h3 className="text-xl font-display font-bold text-luxury-pearl mb-2">
                                End of Month
                            </h3>
                            <p className="text-luxury-pearl/60">
                                Payments collected automatically. Clear transaction history. No awkward money conversations.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
                <div className="card-luxury p-8 sm:p-12 text-center">
                    <h2 className="text-3xl sm:text-4xl font-display font-bold text-luxury-pearl mb-4">
                        Ready to Make Your Life Easier?
                    </h2>
                    <p className="text-lg text-luxury-pearl/60 mb-8 max-w-2xl mx-auto">
                        Join personal trainers who have already saved hundreds of hours using WorldsHighStreet. Choose a plan that fits your business.
                    </p>
                    <button
                        onClick={handleViewPlansClick}
                        className="btn-primary text-lg px-8 py-3 inline-flex items-center gap-2 group"
                    >
                        View Plans & Get Started
                        <ArrowRightIcon className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-luxury-graphite/30 mt-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <p className="text-luxury-pearl/40 text-sm">
                            © 2026 WorldsHighStreet. Making Personal Training Business Easier.
                        </p>
                        <div className="flex gap-6">
                            <button onClick={() => navigate('/')} className="text-luxury-pearl/40 hover:text-emerald-300 text-sm transition-colors">
                                Home
                            </button>
                            <button onClick={handleViewPlansClick} className="text-luxury-pearl/40 hover:text-emerald-300 text-sm transition-colors">
                                Plans
                            </button>
                            <a href="/login" className="text-luxury-pearl/40 hover:text-emerald-300 text-sm transition-colors">
                                Login
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default Marketing;
